import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { MapPin, Plus, Trash2, Check, X, Edit2 } from 'lucide-react';
import { getRooms, createRoom, updateRoom, deleteRoom } from '../../api';
import { useStructure } from '../../contexts/StructureContext';

const EMPTY_FORM = { name: '', area: '' };

const RoomManagement = () => {
  const { currentStructure, structures } = useStructure();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchRooms = useCallback(async () => {
    if (!currentStructure) return;
    try {
      setLoading(true);
      const response = await getRooms(currentStructure.id);
      setRooms(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des pièces');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentStructure]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !currentStructure) return;
    try {
      setLoading(true);
      setError(null);
      await createRoom({ name: form.name.trim(), area: form.area || null, structureId: currentStructure.id });
      resetForm();
      fetchRooms();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Cette pièce existe déjà dans cette structure');
      } else {
        setError("Erreur lors de l'ajout");
        console.error(err);
      }
      setLoading(false);
    }
  };

  const handleEdit = (room) => {
    setForm({ name: room.name, area: room.area !== null && room.area !== undefined ? String(room.area) : '' });
    setEditingId(room.id);
    setShowForm(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      setLoading(true);
      setError(null);
      await updateRoom(editingId, { name: form.name.trim(), area: form.area || null });
      resetForm();
      fetchRooms();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Cette pièce existe déjà dans cette structure');
      } else {
        setError('Erreur lors de la modification');
        console.error(err);
      }
      setLoading(false);
    }
  };

  const handleDelete = async (room) => {
    if (!window.confirm(`Supprimer la pièce "${room.name}" ?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteRoom(room.id);
      fetchRooms();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  if (!currentStructure) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card><CardContent><p className="text-gray-500 py-8 text-center">Sélectionnez une structure dans le sélecteur en haut de page.</p></CardContent></Card>
      </div>
    );
  }

  const inputCls = 'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm';

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin size={20} />
            Pièces / Lieux — {currentStructure.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Ces lieux apparaissent dans le sélecteur "Lieu" lors de la création d'un bon de travaux.
            {structures.length > 1 && (
              <span className="block mt-1 text-xs text-primary">La liste est propre à chaque structure — changez de structure via le sélecteur en haut.</span>
            )}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">{error}</div>
          )}

          {showForm && (
            <form onSubmit={editingId ? handleUpdate : handleAdd} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex gap-2 items-center flex-wrap">
                <input
                  type="text"
                  className={`flex-1 min-w-32 ${inputCls}`}
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Nom de la pièce *"
                  required
                  autoFocus
                />
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className={`w-24 ${inputCls}`}
                  value={form.area}
                  onChange={(e) => setForm(f => ({ ...f, area: e.target.value }))}
                  placeholder="m²"
                />
                <button
                  type="submit"
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50 text-sm"
                  disabled={loading || !form.name.trim()}
                >
                  <Check size={16} /> {editingId ? 'Modifier' : 'Ajouter'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition"
                >
                  <X size={16} />
                </button>
              </div>
            </form>
          )}

          {loading && rooms.length === 0 ? (
            <p className="text-gray-500 py-4 text-center text-sm">Chargement...</p>
          ) : rooms.length === 0 ? (
            <p className="text-gray-500 py-6 text-center text-sm">Aucune pièce configurée pour cette structure.</p>
          ) : (
            <div className="space-y-1">
              {rooms.map((room) => (
                <div
                  key={room.id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 transition group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-gray-800 font-medium">{room.name}</span>
                    {room.area != null && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{room.area} m²</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => handleEdit(room)}
                      className="p-1 text-gray-400 hover:text-primary rounded-full transition"
                      title="Modifier"
                      disabled={loading}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(room)}
                      className="p-1 text-gray-300 hover:text-red-500 rounded-full transition"
                      title="Supprimer"
                      disabled={loading}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!showForm && (
        <button
          onClick={() => { setShowForm(true); setForm(EMPTY_FORM); setEditingId(null); }}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter une pièce"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default RoomManagement;
